import React from 'react';
import { useTranslation } from 'react-i18next';
import RedemptionIcon from '@/assets/icons/redemption-shopping-icon.svg';
import { UserActivity } from '@/types/user.types';
import { GroupedSpecificationItem } from '@/types/groupedHistory.types';
import './HistoryItem.scss';

interface HistoryItemProps {
  item: UserActivity | GroupedSpecificationItem;
  isLastVisible?: boolean;
}

const HistoryItem: React.FC<HistoryItemProps> = ({ item, isLastVisible = false }) => {
  const { t } = useTranslation('home');
  //const [showDetails, setShowDetails] = useState(false);
  
  const isGroupedItem = 'items' in item;
  
  const formatPoints = (points: number, type?: 'Credit' | 'Debit') => {
    // Handle negative zero case
    if (Object.is(points, -0) || points === 0) {
      return '0';
    }
    const formatted = new Intl.NumberFormat('en-US').format(Math.abs(points));
    // For grouped items where points might already be negative
    if (points < 0 || type === 'Debit') {
      return `-${formatted}`;
    }
    return formatted;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const getPointsColor = (points: number, type?: 'Credit' | 'Debit') => {
    return points < 0 || type === 'Debit' ? 'history-item__points--negative' : 'history-item__points--positive';
  };
  
  // const toggleDetails = () => {
  //   if (isGroupedItem) {
  //     setShowDetails(!showDetails);
  //   }
  // };

  // Determine item type for timeline height adjustment
  const getItemTypeClass = () => {
    if (isGroupedItem) return 'history-item--type-grouped';
    if (item.type === 'specification') return 'history-item--type-specification';
    if (item.type === 'engagement') return 'history-item--type-engagement';
    if (item.type === 'tag-submission') return 'history-item--type-tag-submission';
    return '';
  };

  return (
    <div 
      className={`history-item ${isLastVisible ? 'history-item--last-visible' : ''} ${isGroupedItem ? 'history-item--grouped' : ''} ${getItemTypeClass()}`}
      /*onClick={toggleDetails}*/
    >
      <div className="history-item__timeline">
        <div className="history-item__icon">
          <img src={RedemptionIcon} alt="timeline icon" />
        </div>
        <div className="history-item__date">{formatDate(item.date)}</div>
      </div>
      
      <div className="history-item__content">
        <div className="history-item__main">
          <div className="history-item__tags">
            <span className={`history-item__tag history-item__tag--${item.type}`}>{item.type}</span>
            {item.subType && (
              <span className="history-item__tag history-item__tag--sub">{item.subType}</span>
            )}
            {/* {isGroupedItem && (
              <span className="history-item__tag history-item__tag--grouped">
                {(item as GroupedSpecificationItem).items.length} items
              </span>
            )} */}
          </div>

          <div className="history-item__client-info">
            {!isGroupedItem && (
              <h3 className="history-item__title">
                {item.type === 'engagement' && (item as UserActivity).engagementActivityType
                  ? (item as UserActivity).engagementActivityType
                  : item.type === 'specification'
                  ? ''
                  : (item as UserActivity).title
                }
              </h3>
            )}
            {item.type === 'specification' && item.orderHeader && (
              <>
                <p className="history-item__client">
                  <span>{t('history.labels.client')}:</span> {item.orderHeader.customerName} <span className="separator">-</span> Order {item.orderHeader.orderNumber}
                </p>
                <p className="history-item__address">
                  <span>{t('history.labels.projectAddress')}:</span> {item.orderHeader.customerAddressStreet}, {item.orderHeader.customerAddressCity}, {item.orderHeader.customerAddressState} {item.orderHeader.customerAddressZip}
                </p>
                <p className="history-item__dealer">
                  <span>{t('history.labels.dealer')}:</span> {item.orderHeader.dealerName} <span className="separator">-</span> {item.orderHeader.salesRep}
                </p>
              </>
            )}
          </div>
        </div>

        {item.points && (
          <div className="history-item__points">
            {item.type === 'specification' && (
              <>
                <div className="history-item__points-row">
                  <span>{t('history.labels.standardPoints')}</span>
                  <span className={getPointsColor(item.points.standard || 0, item.points.type)}>
                    {formatPoints(item.points.standard || 0, item.points.type)}
                  </span>
                </div>
                <div className="history-item__points-row">
                  <span>{t('history.labels.bonusAccelerator')}</span>
                  <span className={getPointsColor(item.points.bonus || 0, item.points.type)}>
                    {formatPoints(item.points.bonus || 0, item.points.type)}
                  </span>
                </div>
              </>
            )}
            <div className="history-item__points-row history-item__points-row--total">
              <span>{t('history.labels.totalPoints')}</span>
              <span className={getPointsColor(item.points.total, item.points.type)}>
                {formatPoints(item.points.total, item.points.type)}
              </span>
            </div>
            
            {/* Running total for grouped items */}
            {'runningTotal' in item && (
              <div className="history-item__points-row history-item__points-row--running-total">
                <span>{t('history.labels.runningTotal')}</span>
                <span>
                  {item.runningTotal < 0 ? '-' : ''}{new Intl.NumberFormat('en-US').format(Math.abs(item.runningTotal))}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Expandable details for grouped items */}
      {/* {isGroupedItem && showDetails && (
        <div className="history-item__grouped-details">
          <h4>{t('history.labels.groupedItems')}</h4>
          {(item as GroupedSpecificationItem).items.map((subItem, index) => (
            <div key={index} className="history-item__grouped-subitem">
              <div className="history-item__grouped-subitem-title">
                {subItem.title || `Item ${index + 1}`}
              </div>
              <div className="history-item__grouped-subitem-points">
                {subItem.points && (
                  <span className={getPointsColor(subItem.points.total, subItem.points.type)}>
                    {formatPoints(subItem.points.total, subItem.points.type)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )} */}
    </div>
  );
};

export default HistoryItem;
